import classNames from 'classnames';
import { CSSProperties, ForwardedRef, ReactNode, forwardRef, useRef } from 'react';

import {
	Box,
	Button,
	Divider,
	DividerProps,
	Flex,
	Space,
	StyleProp,
	Title,
	TitleProps,
} from '@mantine/core';
import { useMergedRef } from '@mantine/hooks';

import IconArrowLeft from '../../../assets/icons/IconArrowLeft.svg?react';
import IconArrowRight from '../../../assets/icons/IconArrowRight.svg?react';
import { CustomButtonProps } from '../../../types';

import classes from './LayoutRow.module.css';

export type LayoutRowType = 'center' | 'top' | 'bottom' | 'once' | 'transparent';

type LayoutRowProps = {
	className?: string;
	children?: ReactNode;
	type?: LayoutRowType;
	showDivider?: boolean;
	divider?: DividerProps;
	style?: CSSProperties;
	disabled?: boolean;
	title?: {
		title?: TitleProps;
		button?: CustomButtonProps;
	};
	buttons?: CustomButtonProps[];
	buttonsWidget?: ReactNode;
	buttonsDirection?: StyleProp<CSSProperties['flexDirection']>;
	withBackButton?: boolean;
	onClick?: () => void;
};

export const LayoutRow = forwardRef(
	(
		{
			className,
			children,
			divider,
			style,
			title,
			disabled,
			type = 'center',
			showDivider = false,
			buttons,
			buttonsWidget,
			buttonsDirection,
			withBackButton,
			onClick,
		}: LayoutRowProps,
		ref: ForwardedRef<HTMLDivElement>,
	) => {
		const refContainer = useRef<HTMLDivElement | null>(null);
		const withoutDivider = ['bottom', 'once', 'transparent'];
		const resultShowDivider = showDivider && !withoutDivider.includes(type);

		const mergedRef = useMergedRef(ref, refContainer);

		return (
			<Flex
				direction="column"
				ref={mergedRef}
				style={style}
				className={classNames(
					classes.container,
					{
						[classes.center]: type === 'center',
						[classes.top]: type === 'top',
						[classes.bottom]: type === 'bottom',
						[classes.once]: type === 'once',
						[classes.transparent]: type === 'transparent',
						[classes.disabled]: disabled,
					},
					className,
				)}
				onClick={onClick}
			>
				{withBackButton ? <IconArrowLeft width={24} onClick={onClick} /> : <Box />}
				{title && (
					<>
						<Flex justify="space-between" align="center">
							<Title className={classes.title} {...title.title} />
							{title.button && (
								<Button {...title.button} variant="transparent" p={0}>
									<Flex align="center" gap={3}>
										{title.button.children}
										<IconArrowRight width={14} />
									</Flex>
								</Button>
							)}
						</Flex>
						{children && <Space h={16} />}
					</>
				)}
				{children ?? <span className={classes.emptyContent} />}
				{buttons && (
					<Flex mt="md" direction={buttonsDirection} gap={10}>
						{buttonsWidget}
						{buttons?.map((el, index) => (
							<Button {...el} size="lg" fullWidth key={index}>
								{el.children}
							</Button>
						))}
					</Flex>
				)}
				{resultShowDivider && <Divider my={16} {...divider} color="#6670851A" />}
			</Flex>
		);
	},
);

LayoutRow.displayName = 'LayoutRow';
